import { create } from "zustand";
import { api, streamUrl, mediaUrl } from "@/lib/api";

export const audio = new Audio();
audio.preload = "metadata";

let listened = 0;
let lastTick = 0;
let reported = false;

export const usePlayer = create((set, get) => ({
  queue: [],
  index: -1,
  isPlaying: false,
  currentTime: 0,
  duration: 0,
  volume: Number(localStorage.getItem("sonora_vol") ?? 0.8),
  muted: false,
  current: () => get().queue[get().index] || null,

  playTrack: (track, list) => {
    const { queue, index } = get();
    if (queue[index]?.track_id === track.track_id) return get().toggle();
    const q = list && list.length ? list : [track];
    const i = Math.max(0, q.findIndex((t) => t.track_id === track.track_id));
    set({ queue: q, index: i });
    get()._load(q[i]);
  },
  addToQueue: (track) => set((s) => ({ queue: [...s.queue, track], index: s.index < 0 ? 0 : s.index })),
  playAt: (i) => {
    const t = get().queue[i];
    if (!t) return;
    set({ index: i });
    get()._load(t);
  },
  _load: (t) => {
    audio.src = streamUrl(t.track_id);
    audio.volume = get().muted ? 0 : get().volume;
    listened = 0;
    reported = false;
    lastTick = 0;
    set({ currentTime: 0, duration: t.duration || 0 });
    audio.play().catch(() => set({ isPlaying: false }));
    updateMediaSession(t);
  },
  toggle: () => {
    if (!audio.src) return;
    if (audio.paused) audio.play().catch(() => {});
    else audio.pause();
  },
  next: () => {
    const { index, queue } = get();
    if (index < queue.length - 1) get().playAt(index + 1);
    else set({ isPlaying: false });
  },
  prev: () => {
    if (audio.currentTime > 4) return (audio.currentTime = 0);
    const { index } = get();
    if (index > 0) get().playAt(index - 1);
  },
  seek: (sec) => {
    if (!audio.src) return;
    audio.currentTime = sec;
    lastTick = sec;
    set({ currentTime: sec });
  },
  setVolume: (v) => {
    audio.volume = v;
    localStorage.setItem("sonora_vol", v);
    set({ volume: v, muted: v === 0 });
  },
  toggleMute: () => {
    const m = !get().muted;
    audio.volume = m ? 0 : get().volume || 0.8;
    set({ muted: m });
  },
}));

audio.addEventListener("play", () => usePlayer.setState({ isPlaying: true }));
audio.addEventListener("pause", () => usePlayer.setState({ isPlaying: false }));
audio.addEventListener("loadedmetadata", () => usePlayer.setState({ duration: audio.duration || 0 }));
audio.addEventListener("ended", () => usePlayer.getState().next());
audio.addEventListener("timeupdate", () => {
  const t = audio.currentTime;
  const d = t - lastTick;
  if (d > 0 && d < 2) listened += d;
  lastTick = t;
  usePlayer.setState({ currentTime: t });
  const cur = usePlayer.getState().current();
  if (!reported && listened >= 30 && cur) {
    reported = true;
    api.post("/streams", { track_id: cur.track_id, listened_seconds: listened }, { headers: { "X-Traffic-Source": sessionStorage.getItem("sonora_src") || "direct" } }).catch(() => {});
  }
  if ("mediaSession" in navigator && audio.duration && navigator.mediaSession.setPositionState) {
    try { navigator.mediaSession.setPositionState({ duration: audio.duration, position: t, playbackRate: 1 }); } catch (e) { /* noop */ }
  }
});

function updateMediaSession(t) {
  if (!("mediaSession" in navigator)) return;
  const art = mediaUrl(t.cover_url);
  navigator.mediaSession.metadata = new window.MediaMetadata({
    title: t.title, artist: t.artist?.name || "", album: "Soundra",
    artwork: art ? [{ src: art, sizes: "512x512", type: "image/jpeg" }] : [],
  });
  const s = usePlayer.getState();
  navigator.mediaSession.setActionHandler("play", () => audio.play());
  navigator.mediaSession.setActionHandler("pause", () => audio.pause());
  navigator.mediaSession.setActionHandler("nexttrack", () => s.next());
  navigator.mediaSession.setActionHandler("previoustrack", () => s.prev());
  navigator.mediaSession.setActionHandler("seekto", (e) => s.seek(e.seekTime));
}

export async function cacheQueueOffline() {
  const cache = await caches.open("sonora-audio-v1");
  const { queue } = usePlayer.getState();
  let n = 0;
  for (const t of queue.slice(0, 10)) {
    const path = `/api/tracks/${t.track_id}/stream`;
    if (await cache.match(path)) { n++; continue; }
    const res = await fetch(streamUrl(t.track_id), { credentials: "include" });
    if (res.ok) { await cache.put(path, res); n++; }
  }
  return n;
}
